/**
 * The cleaning board's filter bar (CAP-412).
 *
 * Shared by both views (only the jobs that need action, or every cleaning), and
 * spelled the same way on each, so switching view does not mean relearning the
 * controls - and so one deep link means one thing wherever it lands.
 *
 * **Every count comes from the server's facets, over the UNFILTERED set.** A
 * count derived from what is on screen would tell the operator what they can
 * already see, which is the one number they do not need: the point of a count
 * beside a filter is to say how much is behind it before it is clicked.
 */

import { useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { CLEANING_ACTION_KINDS, type CleaningActionKind, type CleaningTeam } from '@/api/client'
import {
  CLEANING_SUBSTATES,
  CREW_UNASSIGNED,
  clearCleaningFilters,
  hasActiveCleaningFilters,
  toggleInList,
  type CleaningFilters,
  type CleaningSubstate,
} from './filters'
import { CLEANING_KIND_META, CLEANING_SUBSTATE_META } from './presentation'

const SELECT_CLASS =
  'flex h-8 rounded-input border border-border bg-surface2 px-2 text-sm text-text ' +
  'focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

const LABEL_CLASS = 'text-[11px] uppercase tracking-wider text-textFaint shrink-0'

/** A label and its control, kept together so a narrow screen never wraps between them. */
const FIELD_CLASS = 'flex items-center gap-2'

export interface CleaningFacets {
  property: Record<string, number>
  team: Record<string, number>
  kind?: Record<string, number>
  substate?: Record<string, number>
}

interface MultiSelectProps<T extends string> {
  label: string
  /** In canonical order: the menu reads top-to-bottom as the table does. */
  options: readonly T[]
  selected: readonly T[]
  labelFor: (value: T) => string
  countFor: (value: T) => number | undefined
  onToggle: (value: T) => void
  onClear: () => void
}

/**
 * A multi-select behind one trigger.
 *
 * Laid flat, nine kinds wrapped to three rows and pushed the table a third of a
 * screen down - on the one screen whose job is showing rows. Folded up, the bar
 * is one row and the trigger still says what is selected, so the filter stays
 * legible without being opened.
 */
function MultiSelect<T extends string>({
  label,
  options,
  selected,
  labelFor,
  countFor,
  onToggle,
  onClear,
}: MultiSelectProps<T>) {
  const triggerLabel =
    selected.length === 0
      ? `All ${label.toLowerCase()}`
      : selected.length === 1
        ? labelFor(selected[0])
        : `${selected.length} selected`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          {triggerLabel}
          {selected.length > 1 && (
            <span className="rounded-full bg-mintGlow px-1.5 text-[11px] tabular-nums text-text">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-textFaint" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[290px]">
        {options.map(option => {
          const count = countFor(option)
          return (
            <DropdownMenuCheckboxItem
              key={option}
              checked={selected.includes(option)}
              // Radix closes on select by default, which makes picking a second
              // value a second trip through the trigger.
              onSelect={event => event.preventDefault()}
              onCheckedChange={() => onToggle(option)}
              // A value with nothing behind it stays listed and stays
              // selectable, just dimmed: hiding it would make the menu's length
              // change as the data does, and "zero" is an answer worth seeing.
              className={count === 0 ? 'opacity-45' : undefined}
            >
              <span className="flex-1">{labelFor(option)}</span>
              {count !== undefined && (
                <span className="ml-3 text-xs tabular-nums text-textFaint">{count}</span>
              )}
            </DropdownMenuCheckboxItem>
          )
        })}
        {selected.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="flex justify-end px-1 pt-1">
              <Button variant="ghost" size="sm" onClick={onClear}>
                Clear
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export interface CleaningFilterBarProps {
  filters: CleaningFilters
  onChange: (filters: CleaningFilters) => void
  teams: CleaningTeam[]
  properties: { id: string; name: string }[]
  facets: CleaningFacets | undefined
  /**
   * How many jobs need action, across the whole portfolio and whatever else is
   * filtered. Beside the view switch, so the amount of outstanding work is
   * never hidden by looking at something else.
   */
  needsActionCount: number | undefined
  /** Rows matching the current filters, and the whole set, for the readout. */
  total: number | undefined
  totalUnfiltered: number | undefined
  shown: number
}

export function CleaningFilterBar({
  filters,
  onChange,
  teams,
  properties,
  facets,
  needsActionCount,
  total,
  totalUnfiltered,
  shown,
}: CleaningFilterBarProps) {
  const set = (patch: Partial<CleaningFilters>) => onChange({ ...filters, ...patch })

  // Sorted by name so the list is scannable, which a KV listing's order is not.
  //
  // A property named in the URL but missing from the list gets an option of its
  // own. The list is active properties only, so a link naming an offboarded
  // house would otherwise render a BLANK select while the filter was still
  // being applied - a narrowed board with no visible reason, which then
  // silently un-narrows the moment any other control is touched.
  const sortedProperties = useMemo(() => {
    const sorted = [...properties].sort((a, b) => a.name.localeCompare(b.name))
    if (filters.property && !sorted.some(property => property.id === filters.property)) {
      sorted.unshift({ id: filters.property, name: `${filters.property} (not listed)` })
    }
    return sorted
  }, [properties, filters.property])

  const narrowed = total !== undefined && totalUnfiltered !== undefined && total !== totalUnfiltered

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* What to show is a filter like the others, not a page of its own: the
          list is the same list, narrowed to what needs doing or not. */}
      <div role="group" aria-label="Show" className={`${FIELD_CLASS} mr-3`}>
        <Button
          size="sm"
          variant={filters.tab === 'actions' ? 'default' : 'outline'}
          aria-pressed={filters.tab === 'actions'}
          className="gap-2"
          onClick={() => set({ tab: 'actions' })}
        >
          Only needs action
          {needsActionCount !== undefined &&
            needsActionCount > 0 &&
            (filters.tab === 'actions' ? (
              // On the filled button the destructive red is mud on green, so
              // the count is a dark pill there and red everywhere else.
              <span className="rounded-full bg-black/30 px-1.5 text-xs font-semibold tabular-nums text-white">
                {needsActionCount}
              </span>
            ) : (
              <Badge variant="destructive">{needsActionCount}</Badge>
            ))}
        </Button>
        <Button
          size="sm"
          variant={filters.tab === 'all' ? 'default' : 'outline'}
          aria-pressed={filters.tab === 'all'}
          onClick={() => set({ tab: 'all' })}
        >
          All cleanings
        </Button>
      </div>

      <div className={FIELD_CLASS}>
        <span className={LABEL_CLASS}>Property</span>
        <label className="sr-only" htmlFor="cleaning-property">
          Property
        </label>
        <select
          id="cleaning-property"
          className={SELECT_CLASS}
          value={filters.property}
          onChange={event => set({ property: event.target.value })}
        >
          <option value="">All properties</option>
          {sortedProperties.map(property => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
      </div>

      <div className={FIELD_CLASS}>
        <span className={LABEL_CLASS}>Crew</span>
        <label className="sr-only" htmlFor="cleaning-crew">
          Crew
        </label>
        <select
          id="cleaning-crew"
          className={SELECT_CLASS}
          value={filters.crew}
          onChange={event => set({ crew: event.target.value })}
        >
          <option value="">Any crew</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>
              {team.short_name || team.name}
            </option>
          ))}
          <option value={CREW_UNASSIGNED}>No crew</option>
        </select>
      </div>

      {filters.tab === 'actions' ? (
        <div className={FIELD_CLASS}>
          <span className={LABEL_CLASS}>What</span>
          <MultiSelect<CleaningActionKind>
            label="kinds"
            options={CLEANING_ACTION_KINDS}
            selected={filters.kinds}
            labelFor={kind => CLEANING_KIND_META[kind].label}
            countFor={kind => facets?.kind?.[kind] ?? 0}
            onToggle={kind =>
              set({ kinds: toggleInList(filters.kinds, kind, CLEANING_ACTION_KINDS) })
            }
            onClear={() => set({ kinds: [] })}
          />
        </div>
      ) : (
        <div className={FIELD_CLASS}>
          {/* "Status", not "State": that is what the job lifecycle is called
              everywhere else an operator reads it. */}
          <span className={LABEL_CLASS}>Status</span>
          <MultiSelect<CleaningSubstate>
            label="statuses"
            options={CLEANING_SUBSTATES}
            selected={filters.substates}
            labelFor={substate => CLEANING_SUBSTATE_META[substate].label}
            countFor={substate => facets?.substate?.[substate] ?? 0}
            onToggle={substate =>
              set({ substates: toggleInList(filters.substates, substate, CLEANING_SUBSTATES) })
            }
            onClear={() => set({ substates: [] })}
          />
        </div>
      )}

      {hasActiveCleaningFilters(filters) && (
        <Button variant="ghost" size="sm" onClick={() => onChange(clearCleaningFilters(filters))}>
          Clear filters
        </Button>
      )}

      <span className="ml-2 text-sm tabular-nums text-textMuted">
        <span className="font-semibold text-text">{shown}</span> of{' '}
        <span className="font-semibold text-text">{total ?? 0}</span>
        {/* Said out loud whenever the view is narrowed, so filtering can never
            make the amount of outstanding work look smaller than it is. */}
        {narrowed && <span className="text-textFaint"> filtered from {totalUnfiltered}</span>}
      </span>
    </div>
  )
}
